import { asciiTextValidation } from 'framework/form';

const projectInitialValidationShema = {
  budget: {
    type: 'object',
    required: true,
    properties: {
      billingType: {
        type: 'string',
        required: true,
      },
      fte: {
        type: 'string',
        required: false,
      },
      hours: {
        type: 'number',
        required: {
          value: true,
        },
      },
      amount: {
        type: 'float',
        required: true,
        minValue: 0.01,
      },
      startDate: {
        type: 'string',
        required: true,
      },
      softStartDate: {
        type: 'date',
        required: false,
      },
      endDate: {
        type: 'string',
        required: true,
        minDate: {
          value: '$budget.startDate',
          message: 'Please choose date after start date',
        },
      },
      extendedEndDate: {
        type: 'date',
        required: false,
      },
      expectedSowSigningDate: {
        type: 'string',
        required: {
          value: true,
          when: {
            '$sow.docusignLink': {
              empty: true,
            },
          },
        },
      },
    },
  },
  customerReport: {
    type: 'object',
    required: {
      value: true,
      when: {
        '$project.createDaVinciRedmine': {
          eq: true,
        },
      },
    },
    properties: {
      isSystemReportUsed: {
        type: 'string',
        required: {
          value: true,
          when: {
            '$project.createDaVinciRedmine': {
              eq: true,
            },
          },
        },
      },
      reportFrequency: {
        type: 'string',
        required: {
          value: true,
          when: {
            '$project.createDaVinciRedmine': {
              eq: true,
            },
          },
        },
      },
      approvalType: {
        type: 'string',
        required: {
          value: true,
          when: {
            _and_: [
              {
                '$project.createDaVinciRedmine': {
                  eq: true,
                },
              },
              {
                '$customerReport.isSystemReportUsed': {
                  ne: 'false',
                },
              },
            ],
          },
        },
      },
    },
  },
  sow: {
    type: 'object',
    required: false,
    properties: {
      isDocusignLink: {
        type: 'boolean',
        required: true,
      },
      docusignLink: {
        type: 'string',
        required: {
          value: true,
          when: {
            _and_: [
              {
                '$sow.isDocusignLink': {
                  eq: false,
                },
              },
              {
                '$sow.docusignLink': {
                  ne: undefined,
                },
              },
            ],
          },
        },
      },
      relatedAttachments: {
        type: 'array',
        required: {
          value: true,
          when: {
            _and_: [
              {
                '$sow.isDocusignLink': {
                  eq: false,
                },
              },
              {
                '$sow.docusignLink': {
                  ne: undefined,
                },
              },
            ],
          },
        },
      },
      sowId: {
        type: 'number',
        required: {
          value: true,
          when: {
            _and_: [
              {
                '$sow.isDocusignLink': {
                  eq: false,
                },
              },
              {
                '$sow.docusignLink': {
                  ne: undefined,
                },
              },
            ],
          },
        },
        minValue: 1,
      },
    },
  },
  project: {
    type: 'object',
    required: true,
    properties: {
      scenario: {
        type: 'string',
        required: true,
      },
      originalName: {
        type: 'string',
        required: {
          value: true,
          when: {
            '$project.createNewProject': {
              eq: true,
            },
            '$project.createDaVinciRedmine': {
              eq: true,
            },
          },
        },
        maxSize: 255,
        ...asciiTextValidation,
      },
      projectCodeName: {
        type: 'string',
        required: {
          value: true,
          when: {
            '$project.createNewProject': {
              eq: true,
            },
            '$project.createDaVinciRedmine': {
              eq: true,
            },
          },
        },
        maxSize: 255,
        ...asciiTextValidation,
      },
      isAccountCodeName: {
        type: 'boolean',
      },
      engagementType: {
        type: 'string',
        required: {
          value: true,
          when: {
            '$project.createDaVinciRedmine': {
              eq: true,
            },
          },
        },
      },
      existingProject: {
        type: 'object',
        required: {
          value: true,
          when: {
            _or_: [
              {
                '$project.createDaVinciRedmine': {
                  eq: true,
                },
                '$project.createNewProject': {
                  eq: false,
                },
              },
              {
                '$project.createDaVinciRedmine': {
                  eq: false,
                },
              },
            ],
          },
        },
        properties: {
          id: {
            type: 'string',
            required: {
              value: true,
              when: {
                _or_: [
                  {
                    '$project.createDaVinciRedmine': {
                      eq: true,
                    },
                    '$project.createNewProject': {
                      eq: false,
                    },
                  },
                  {
                    '$project.createDaVinciRedmine': {
                      eq: false,
                    },
                  },
                ],
              },
            },
          },
        },
      },
    },
  },
  inputData: {
    type: 'object',
    required: true,
    properties: {
      opportunityId: {
        type: 'string',
        required: true,
      },
      accountId: {
        type: 'string',
        required: true,
      },
    },
  },
  team: {
    type: 'object',
    required: true,
    properties: {
      expectedStructure: {
        type: 'array',
        required: true,
        minSize: 1,
        forEach: {
          type: 'object',
          required: true,
          properties: {
            competencyLevel: {
              type: 'string',
              required: true,
            },
            positionId: {
              type: 'string',
              required: true,
            },
            requiredFte: {
              type: 'number',
              required: true,
              minValue: 0.01,
            },
            requiredRole: {
              type: 'string',
              required: true,
            },
            technologyId: {
              type: 'enum',
              required: {
                value: true,
                when: [
                  {
                    '$.positionId': {
                      in: ['47', 47],
                    },
                  },
                ],
              },
            },
          },
        },
      },
    },
  },
};

export default projectInitialValidationShema;
